import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { LineItemTable } from "@portal/BaseComponents/InvoiceLineItems/LineItemTable/LineItemTable";
import { AmountText } from "@portal/BaseComponents/Table/Components/Amount/AmountText";
import { DateCell } from "@portal/BaseComponents/Table/Components/Date/DateCell";
import { DueStatus } from "@portal/BaseComponents/Table/Components/DueStatus/DueStatus";

import { cellComponents } from "@portal/BaseComponents/Table/Components/cellComponents";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import {
  getInvoiceStatusConfig,
  invoiceStatusesConfig,
} from "@portal/Components/Portal/PotalTabs/Invoice/predicate";
import { useFormats } from "@portal/hooks/use-formats";
import { InvoiceDetails } from "@portal/types/entities/invoice";
import { StatusTag } from "@sinecycle/growcomponents";
import { DescriptionsProps } from "antd";
import SkeletonInput from "antd/es/skeleton/Input";
import { DetailsViewLayout } from "../../DetailsView";

interface InvoiceDetailsProps {
  data?: InvoiceDetails;
  loading: boolean;
}

function InvoiceDetailsComp(props: InvoiceDetailsProps) {
  const { data, loading } = props;
  const { dateformat } = useFormats();
  const Title = loading ? (
    <SkeletonInput active />
  ) : (
    <TypographyText>{data?.number ?? "-"}</TypographyText>
  );

  const Status =
    !loading && data ? (
      <StatusTag
        style={getInvoiceStatusConfig(data.status).style}
        text={getInvoiceStatusConfig(data.status).label}
      />
    ) : (
      <SkeletonInput active />
    );

  const IssueDate =
    data && !loading ? (
      <DateCell value={data.date} />
    ) : (
      <SkeletonInput active />
    );
  const DueDate =
    data && !loading ? (
      <>
        {cellComponents.extraInfo({
          value: {
            has_info: !invoiceStatusesConfig.CLOSED.includes(data.status),
            value: data.due_date,
          },
          infoType: "DATE",
          extraComponent: <DueStatus date={data.due_date} />,
          dateCellProps: {
            format: dateformat,
          },
        } as any)}
      </>
    ) : (
      <SkeletonInput active />
    );
  const Balance =
    data && !loading ? (
      <AmountText
        amount={data.balance_due_amount}
        textProps={{ strong: true }}
        format="FULL"
        currency={data.currency}
      />
    ) : (
      <SkeletonInput active />
    );
  const TotalAmount =
    data && !loading ? (
      <AmountText amount={data.amount} format="FULL" currency={data.currency} />
    ) : (
      <SkeletonInput active />
    );
  const DisputedAmount =
    data && !loading ? (
      <AmountText
        amount={data.status === "DISPUTE" ? data.balance_due_amount : 0}
        format="FULL"
        currency={data.currency}
      />
    ) : (
      <SkeletonInput active />
    );
  const PTPBalance =
    data && !loading ? (
      <AmountText
        amount={data?.ptp_balance_amount ?? 0}
        format="FULL"
        currency={data.currency}
      />
    ) : (
      <SkeletonInput active />
    );

  function getItems(): DescriptionsProps["items"] {
    return [
      {
        key: "TITLE",
        label: "Title",
        children: Title,
        className: "ello",
      },
      { key: "STATUS", label: "Status", children: Status },
      { key: "ISSUE_DATE", label: "Issue Date", children: IssueDate },
      { key: "DUE_DATE", label: "Due Date", children: DueDate },
      { key: "BALANCE", label: "Balance", children: Balance },
      { key: "TOTAL_AMOUNT", label: "Total Amount", children: TotalAmount },
      {
        key: "DISPUTED_AMOUNT",
        label: "Disputed Amount",
        children: DisputedAmount,
      },
      { key: "PTP_BALANCE", label: "PTP Balance", children: PTPBalance },
    ];
  }

  const items = getItems();

  return (
    <Flex vertical space="24">
      <DetailsViewLayout items={items} />
      <LineItemTable loading={loading} lineItem={data?.line_item_list} />
    </Flex>
  );
}

export { InvoiceDetailsComp };
